import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Suggestion {
  id: string;
  label: string;
  text: string;
  icon?: string;
}

interface ContextData {
  pendingBetonnages: number;
  deliveriesToday: number;
  pendingReservations: number;
  unresolvedIncidents: number;
  ouvragesSansCommentaireEcart: number;
}

interface UseContextualSuggestionsOptions {
  siteId: string | undefined;
  lastAction?: string;
}

export function useContextualSuggestions({ siteId, lastAction }: UseContextualSuggestionsOptions) {
  // Fetch contextual data in a single query for efficiency
  const { data: contextData } = useQuery({
    queryKey: ["contextual-suggestions-data", siteId],
    queryFn: async (): Promise<ContextData> => {
      if (!siteId) return { pendingBetonnages: 0, deliveriesToday: 0, pendingReservations: 0, unresolvedIncidents: 0, ouvragesSansCommentaireEcart: 0 };
      
      const today = new Date().toISOString().split("T")[0];
      
      // Parallel fetch for all contextual data
      const [betonnagesResult, reservationsResult, incidentsResult, progressResult] = await Promise.all([
        // Pending/scheduled betonnages today
        supabase
          .from("concrete_pours")
          .select("id, status")
          .eq("site_id", siteId)
          .eq("delivery_date", today)
          .is("deleted_at", null)
          .in("status", ["pending", "scheduled", "in_progress"]),
        
        // Pending reservations (awaiting confirmation)
        supabase
          .from("concrete_pours")
          .select("id")
          .eq("site_id", siteId)
          .is("deleted_at", null)
          .eq("status", "awaiting_confirmation"),
        
        // Unresolved incidents
        supabase
          .from("concrete_pours")
          .select("id, concrete_deliveries!inner(id)")
          .eq("site_id", siteId)
          .is("deleted_at", null)
          .then(async (poursResult) => {
            if (poursResult.error || !poursResult.data) return { data: [], error: null };
            const deliveryIds = poursResult.data.flatMap((p: any) => 
              (p.concrete_deliveries || []).map((d: any) => d.id)
            );
            if (deliveryIds.length === 0) return { data: [], error: null };
            return supabase
              .from("delivery_incidents")
              .select("id")
              .in("truck_id", deliveryIds)
              .eq("resolved", false);
          }),
        // Ouvrages en progression sans commentaire d'écart (pour suggestion proactive "Commentaire ouvrage")
        supabase
          .from("work_progress")
          .select("work_item_id, deviation_comment", { count: "exact", head: true })
          .eq("site_id", siteId)
          .is("deviation_comment", null)
      ]);

      const betonnages = betonnagesResult.data || [];
      const deliveriesToday = betonnages.filter(b => b.status === "in_progress").length;
      const ouvragesSansCommentaireEcart = progressResult?.count ?? 0;
      
      return {
        pendingBetonnages: betonnages.length,
        deliveriesToday,
        pendingReservations: reservationsResult.data?.length || 0,
        unresolvedIncidents: incidentsResult.data?.length || 0,
        ouvragesSansCommentaireEcart,
      };
    },
    enabled: !!siteId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const suggestions = useMemo<Suggestion[]>(() => {
    const result: Suggestion[] = [];
    const hour = new Date().getHours();
    const ctx = contextData || { pendingBetonnages: 0, deliveriesToday: 0, pendingReservations: 0, unresolvedIncidents: 0, ouvragesSansCommentaireEcart: 0 };

    // Outils principaux toujours proposés en premier (jamais tronqués)
    const baseTools: Suggestion[] = [
      { id: "order-concrete", label: "Commander du béton", text: "Je veux commander du béton", icon: "truck" },
      { id: "calc-volume", label: "Calculer un besoin volumétrique", text: "Calculer le besoin en béton / Quel est le besoin volumétrique ? (ou donner des dimensions L×l×h)", icon: "chart" },
      { id: "reserve-trucks", label: "Réserver des camions", text: "Je veux réserver des camions toupie", icon: "truck" },
      { id: "check-progress", label: "Voir l'avancement", text: "Quel est l'avancement du chantier ?", icon: "chart" },
      { id: "update-progress", label: "Mettre à jour l'avancement", text: "Je veux mettre à jour l'avancement d'un ouvrage", icon: "check" },
      { id: "comment-ouvrage", label: "Commentaire ouvrage", text: "Je veux ajouter un commentaire sur un ouvrage (écart ou suivi)", icon: "check" },
      { id: "verifier-bl", label: "Vérifier un BL", text: "Où en est le bon de livraison ? / Vérifier le BL du camion", icon: "list" },
      { id: "synthese-chantier", label: "Synthèse chantier", text: "Quelle est la synthèse du chantier ?", icon: "chart" },
      { id: "cancel-order", label: "Annuler une commande", text: "Je veux annuler une commande ou une réservation", icon: "alert-triangle" },
    ];
    for (const s of baseTools) {
      if (!result.find((r) => r.id === s.id)) result.push(s);
    }

    // Proactive: ouvrages sans commentaire d'écart (suggestion prioritaire si écart potentiel)
    if (ctx.ouvragesSansCommentaireEcart > 0 && !result.find((s) => s.id === "comment-ouvrage-proactif")) {
      result.push({
        id: "comment-ouvrage-proactif",
        label: `${ctx.ouvragesSansCommentaireEcart} ouvrage(s) sans commentaire`,
        text: "Je veux ajouter un commentaire sur un ouvrage (écart ou suivi)",
        icon: "check",
      });
    }

    // Priority 1: Unresolved incidents
    if (ctx.unresolvedIncidents > 0) {
      result.push({
        id: "check-incidents",
        label: `${ctx.unresolvedIncidents} incident${ctx.unresolvedIncidents > 1 ? "s" : ""} à résoudre`,
        text: "Quels sont les incidents non résolus ?",
        icon: "alert-triangle",
      });
    }

    // Priority 2: Pending reservations
    if (ctx.pendingReservations > 0) {
      result.push({
        id: "check-reservations",
        label: `${ctx.pendingReservations} résa${ctx.pendingReservations > 1 ? "s" : ""} en attente`,
        text: "Quelles sont les réservations en attente de confirmation ?",
        icon: "clock",
      });
    }

    // Priority 3: Deliveries today
    if (ctx.deliveriesToday > 0) {
      result.push({
        id: "check-today",
        label: `${ctx.deliveriesToday} livraison${ctx.deliveriesToday > 1 ? "s" : ""} en cours`,
        text: "Quelles sont les livraisons en cours aujourd'hui ?",
        icon: "truck",
      });
    } else if (ctx.pendingBetonnages > 0) {
      result.push({
        id: "check-today",
        label: `Voir les ${ctx.pendingBetonnages} bétonnages du jour`,
        text: "Quelles sont les livraisons prévues aujourd'hui ?",
        icon: "list",
      });
    }

    // Time-based
    if (hour >= 6 && hour < 10 && !result.find((s) => s.id === "morning-order")) {
      result.push({
        id: "morning-order",
        label: "Commander pour aujourd'hui",
        text: "Je veux commander du béton pour aujourd'hui",
        icon: "truck",
      });
    } else if (hour >= 14 && hour < 18 && !result.find((s) => s.id === "afternoon-order")) {
      result.push({
        id: "afternoon-order",
        label: "Commander pour demain",
        text: "Je veux commander du béton pour demain",
        icon: "calendar",
      });
    }

    // Last action context
    if (lastAction === "order_created" && !result.find((s) => s.id === "another-order")) {
      result.push({
        id: "another-order",
        label: "Commander autre chose",
        text: "Je veux passer une autre commande",
        icon: "plus",
      });
    }
    if (lastAction === "progress_updated" && !result.find((s) => s.id === "more-progress")) {
      result.push({
        id: "more-progress",
        label: "Mettre à jour un autre avancement",
        text: "Je veux mettre à jour l'avancement d'un autre ouvrage",
        icon: "check",
      });
    }

    // Toujours garder tous les baseTools, puis jusqu'à 4 suggestions contextuelles (max 10 au total)
    const baseIds = new Set(baseTools.map((s) => s.id));
    const contextual = result.filter((s) => !baseIds.has(s.id));
    return [...baseTools, ...contextual.slice(0, 4)];
  }, [contextData, lastAction]);

  return { suggestions };
}
